import React from "react";
import { ChevronLeft, ChevronRight, Gavel } from "lucide-react";
import { Link } from "react-router-dom";
import { getActiveAuctions } from "../../../../models/auctions";
import i18n from "../../../../i18n";
import HeaderBanner from '../../../common/HeaderBanner';
import '../../../../styles/components/auction-calendar.scss'

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

export class AuctionCalendar extends React.Component {
    constructor(props) {
        super(props);
        const now = new Date();
        this.state = {
            auctions: [],
            loading: true,
            currentYear: now.getFullYear(),
            currentMonth: now.getMonth(),
            selectedDate: this.dateKey(now),
            selectedAuctions: [],
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.loadAuctions();
    }

    async loadAuctions() {
        try {
            const response = await getActiveAuctions();
            if (response.success) {
                this.setState({ auctions: response.data, loading: false });
            } else {
                this.setState({ loading: false });
            }
        } catch {
            this.setState({ loading: false });
        }
    }

    createDate(dt) {
        return dt ? new Date(dt.replace(/-/g, "/")) : null;
    }

    // Returns "YYYY-MM-DD" key from a Date
    dateKey(date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }

    // Build a map: dateKey → [auction, ...]
    buildAuctionMap() {
        const map = {};
        this.state.auctions.forEach((auc) => {
            const start = this.createDate(auc.start_date);
            const end = this.createDate(auc.end_date);
            if (!start || !end) return;
            const cur = new Date(start);
            cur.setHours(0, 0, 0, 0);
            const endDay = new Date(end);
            endDay.setHours(0, 0, 0, 0);
            while (cur <= endDay) {
                const k = this.dateKey(cur);
                if (!map[k]) map[k] = [];
                map[k].push(auc);
                cur.setDate(cur.getDate() + 1);
            }
        });
        return map;
    }

    prevMonth() {
        this.setState((s) => {
            const d = new Date(s.currentYear, s.currentMonth - 1, 1);
            return { currentYear: d.getFullYear(), currentMonth: d.getMonth(), selectedDate: null, selectedAuctions: [] };
        });
    }

    nextMonth() {
        this.setState((s) => {
            const d = new Date(s.currentYear, s.currentMonth + 1, 1);
            return { currentYear: d.getFullYear(), currentMonth: d.getMonth(), selectedDate: null, selectedAuctions: [] };
        });
    }

    selectDay(dateKey, auctions) {
        this.setState({ selectedDate: dateKey, selectedAuctions: auctions || [] });
    }

    formatTime(dt) {
        if (!dt) return "";
        const d = this.createDate(dt);
        const h = d.getHours(), m = String(d.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        return `${h % 12 || 12}:${m} ${ampm}`;
    }

    render() {
        const { currentYear, currentMonth, selectedDate, selectedAuctions, loading } = this.state;
        const lng = localStorage.getItem("I18N_LANGUAGE");
        const aucMap = this.buildAuctionMap();
        const today = this.dateKey(new Date());

        // Calendar grid
        const firstDay = new Date(currentYear, currentMonth, 1).getDay();
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

        return (
            <React.Fragment>
                <HeaderBanner
                    title={i18n.t('auction_calendar')}
                    subtitle={i18n.t('auction_calendar_subtitle')}
                />
                <div className="re-cal-page">
                    <div className="re-cal-wrapper">

                        {/* ── Header ── */}
                        <div className="re-cal-header">
                            <Gavel size={20} className="re-cal-header__icon" />
                            <h2 className="re-cal-header__title">{i18n.t("auction_calendar")}</h2>
                        </div>

                        <div className="re-cal-layout">

                            {/* ── Calendar panel ── */}
                            <div className="re-cal-panel">
                                <div className="re-cal-nav">
                                    <button className="re-cal-nav__btn" onClick={() => this.prevMonth()}>
                                        <ChevronLeft size={18} />
                                    </button>
                                    <span className="re-cal-nav__label">
                                        {i18n.t(MONTHS[currentMonth])} {currentYear}
                                    </span>
                                    <button className="re-cal-nav__btn" onClick={() => this.nextMonth()}>
                                        <ChevronRight size={18} />
                                    </button>
                                </div>

                                {/* Day-of-week headers */}
                                <div className="re-cal-grid re-cal-grid--head">
                                    {DAYS.map((d) => (
                                        <div key={d} className="re-cal-cell re-cal-cell--head">{d}</div>
                                    ))}
                                </div>

                                {/* Day cells */}
                                <div className="re-cal-grid">
                                    {cells.map((day, idx) => {
                                        if (!day) return <div key={`e-${idx}`} className="re-cal-cell re-cal-cell--empty" />;
                                        const key = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                        const dayAucs = aucMap[key] || [];
                                        const hasAuc = dayAucs.length > 0;
                                        const isToday = key === today;
                                        const isSel = key === selectedDate;

                                        return (
                                            <div
                                                key={key}
                                                className={[
                                                    "re-cal-cell",
                                                    isToday ? "re-cal-cell--today" : "",
                                                    isSel ? "re-cal-cell--selected" : "",
                                                    hasAuc ? "re-cal-cell--has-auc" : "",
                                                ].join(" ")}
                                                onClick={() => this.selectDay(key, dayAucs)}
                                            >
                                                <span className="re-cal-cell__num">{day}</span>
                                                {hasAuc && (
                                                    <span className="re-cal-cell__dot-row">
                                                        {dayAucs.slice(0, 3).map((_, i) => (
                                                            <span key={i} className="re-cal-cell__dot" />
                                                        ))}
                                                    </span>
                                                )}
                                                {hasAuc && (
                                                    <span className="re-cal-cell__count">{dayAucs.length}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="re-cal-legend">
                                    <span className="re-cal-legend__item">
                                        <span className="re-cal-legend__dot re-cal-legend__dot--today" /> {i18n.t("today") || "Today"}
                                    </span>
                                    <span className="re-cal-legend__item">
                                        <span className="re-cal-legend__dot re-cal-legend__dot--auc" /> {i18n.t("active") || "Active auction"}
                                    </span>
                                </div>
                            </div>

                            {/* ── Sidebar: auction list ── */}
                            <div className="re-cal-sidebar">
                                {loading && (
                                    <div className="re-cal-sidebar__empty">
                                        <div className="re-cal-spinner" />
                                        <p>{i18n.t("loading") || "Loading..."}</p>
                                    </div>
                                )}

                                {!loading && !selectedDate && (
                                    <div className="re-cal-sidebar__empty">
                                        <Gavel size={36} opacity={0.2} />
                                        <p>{i18n.t("select_date") || "Select a date to see auctions"}</p>
                                    </div>
                                )}

                                {!loading && selectedDate && selectedAuctions.length === 0 && (
                                    <div className="re-cal-sidebar__empty">
                                        <Gavel size={36} opacity={0.2} />
                                        <p>{i18n.t("no_auctions") || "No auctions on this date"}</p>
                                    </div>
                                )}

                                {!loading && selectedDate && selectedAuctions.length > 0 && (
                                    <>
                                        <div className="re-cal-sidebar__title">
                                            {selectedAuctions.length} {i18n.t("auctions") || "Auctions"} — {selectedDate}
                                        </div>
                                        <div className="re-cal-sidebar__list">
                                            {selectedAuctions.map((auc) => {

                                                const title = auc.is_a_group
                                                    ? (lng === "ar" ? (auc.group_name_ar || auc.group_name) : (auc.group_name || auc.group_name_ar))
                                                    : (lng === "ar" ? (auc.title_ar || auc.title) : (auc.title || auc.title_ar));
                                                const img = auc.file_auction_image_full || auc.image || "";
                                                return (
                                                    <Link
                                                        key={auc.id}
                                                        to={auc.is_a_group ? `/grouped-auctions/${auc.group}` : `/auction-details/${auc.id}`}
                                                        className="re-cal-auc-card"
                                                    >
                                                        {img && (
                                                            <img src={img} alt={title} className="re-cal-auc-card__img" />
                                                        )}
                                                        <div className="re-cal-auc-card__body">
                                                            <div className="re-cal-auc-card__id">MZAD-{auc.id}</div>
                                                            <div className="re-cal-auc-card__title">{title}</div>
                                                            <div className="re-cal-auc-card__meta">
                                                                {/* <span>
                                                                    <Clock size={11} /> {this.formatDate(auc.end_date)}
                                                                </span> */}
                                                            </div>
                                                            {/* <div className="re-cal-auc-card__amount">
                                                                {formatCurrency(auc.start_amount)}  <OmrRiyal />
                                                            </div> */}
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}